import type { Branding } from "@shared/branding";
import type { ErrorCode } from "@shared/errors";
import type {
  TaskRow,
  ProjectRow,
  CommentRow,
  TagRow,
  AttachmentRow,
  FileRow,
  ProjectFileRow,
  TeamMemberRow,
  ActivityRow,
  UserDTO,
  TaskStatus,
  ProjectKind,
  UpdateTaskInput,
  MetricsDashboardResponseT,
  TaskActivityResponse,
  ProjectMembersResponse,
} from "@shared/types";
import type { ActivityItem } from "@/components/ActivityFeed";

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: { "content-type": "application/json", ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: {
        code?: ErrorCode | "unknown";
        message?: string;
        fields?: Record<string, string[]>;
      };
    };
    throw new ApiError(
      res.status,
      body?.error?.code ?? "unknown",
      body?.error?.message ?? res.statusText,
      body?.error?.fields,
    );
  }
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: ErrorCode | "unknown",
    message: string,
    public fields?: Record<string, string[]>,
  ) {
    super(message);
  }
}

export const api = {
  branding: () => request<Branding>("/api/branding"),
  capabilities: () =>
    request<{ attachments: boolean; email: boolean }>("/api/capabilities"),
  me: () => request<{ user: UserDTO }>("/api/me"),

  // Public — resolves a `?invite=<token>` URL param to a pre-filled email
  // + inviter name. Returns null on 404 (expired / consumed / unknown)
  // so the login page can fall back to the regular email entry flow.
  inviteePreview: async (
    inviteId: string,
  ): Promise<{ email: string; invitedBy: string | null } | null> => {
    try {
      return await request<{ email: string; invitedBy: string | null }>(
        `/api/i/${encodeURIComponent(inviteId)}/preview`,
      );
    } catch {
      return null;
    }
  },

  // Projects
  listProjects: () => request<{ projects: ProjectRow[] }>("/api/projects"),
  getProject: (id: string) =>
    request<{ project: ProjectRow }>(`/api/projects/${id}`),
  createProject: (body: {
    name: string;
    kind: ProjectKind;
    description?: string;
    targetDate?: string;
    color?: string;
  }) =>
    request<{ project: ProjectRow }>("/api/projects", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  patchProject: (
    id: string,
    body: Partial<{
      name: string;
      description: string;
      status: string;
      kind: ProjectKind;
      color: string;
      targetDate: string | null;
      tagNames: string[];
      healthOverride: "green" | "amber" | "red" | null;
      healthReason: string | null;
    }>,
  ) =>
    request<{ ok: true }>(`/api/projects/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteProject: (id: string) =>
    request<{ ok: true }>(`/api/projects/${id}`, { method: "DELETE" }),
  reorderProjects: (orderedIds: string[]) =>
    request<{ ok: true; updated: number }>("/api/projects/reorder", {
      method: "PATCH",
      body: JSON.stringify({ orderedIds }),
    }),
  // Full member roster (with role + email). Used by the Members tab; the
  // list+recent rows expose a separate `members[]` preview capped at 3.
  projectMembers: (projectId: string) =>
    request<ProjectMembersResponse>(`/api/projects/${projectId}/members`),
  // Settings page — owner-only member management.
  addProjectMember: (projectId: string, userId: string) =>
    request<{ ok: true }>(`/api/projects/${projectId}/members`, {
      method: "POST",
      body: JSON.stringify({ userId }),
    }),
  removeProjectMember: (projectId: string, userId: string) =>
    request<{ ok: true }>(`/api/projects/${projectId}/members/${userId}`, {
      method: "DELETE",
    }),
  transferProjectOwnership: (projectId: string, userId: string) =>
    request<{ ok: true }>(`/api/projects/${projectId}/transfer-ownership`, {
      method: "POST",
      body: JSON.stringify({ userId }),
    }),

  // Tasks
  listTasks: (projectId: string) =>
    request<{ tasks: TaskRow[] }>(`/api/projects/${projectId}/tasks`),
  createTask: (
    projectId: string,
    body: {
      title: string;
      priority?: number;
      status?: TaskStatus;
      parentTaskId?: string | null;
      description?: string;
      // Server accepts a single assigneeId on create; multi-assign happens
      // post-create via the watchers / patch endpoints.
      assigneeId?: string | null;
      dueDate?: string | null;
      startDate?: string | null;
      estimate?: number | null;
      type?: "task" | "story" | "bug";
    },
  ) =>
    request<{ task: TaskRow }>(`/api/projects/${projectId}/tasks`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  patchTask: (taskId: string, body: UpdateTaskInput) =>
    request<{ ok: true }>(`/api/tasks/${taskId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteTask: (taskId: string) =>
    request<{ ok: true }>(`/api/tasks/${taskId}`, { method: "DELETE" }),
  // Move a top-level task (and its subtasks) to another project.
  moveTask: (taskId: string, projectId: string) =>
    request<{ ok: true; projectId: string }>(`/api/tasks/${taskId}/move`, {
      method: "POST",
      body: JSON.stringify({ projectId }),
    }),
  getTask: (taskId: string) =>
    request<{ task: TaskRow & { tags?: TagRow[] } }>(`/api/tasks/${taskId}`),

  // Task dependencies (blocks / blocked-by)
  taskDependencies: (taskId: string) =>
    request<import("@shared/types").TaskDependenciesResponse>(
      `/api/tasks/${taskId}/dependencies`,
    ),
  addTaskBlocker: (taskId: string, blockerId: string) =>
    request<{ ok: true }>(`/api/tasks/${taskId}/blockers`, {
      method: "POST",
      body: JSON.stringify({ blockerId }),
    }),
  removeTaskBlocker: (taskId: string, blockerId: string) =>
    request<{ ok: true }>(`/api/tasks/${taskId}/blockers/${blockerId}`, {
      method: "DELETE",
    }),

  // All blocker→blocked edges in a project (for the timeline connectors).
  projectDependencies: (projectId: string) =>
    request<{ edges: { blockerId: string; blockedId: string }[] }>(
      `/api/projects/${projectId}/dependencies`,
    ),

  // Every milestone across the user's projects (workspace Roadmap).
  // Dashboards
  listDashboards: () =>
    request<{ dashboards: import("@shared/types").DashboardRow[] }>(
      "/api/dashboards",
    ),
  createDashboard: (name: string, shared: boolean) =>
    request<{ dashboard: { id: string } }>("/api/dashboards", {
      method: "POST",
      body: JSON.stringify({ name, shared }),
    }),
  getDashboard: (id: string) =>
    request<{ widgets: import("@shared/types").DashboardWidgetRow[] }>(
      `/api/dashboards/${id}`,
    ),
  deleteDashboard: (id: string) =>
    request<{ ok: true }>(`/api/dashboards/${id}`, { method: "DELETE" }),
  addWidget: (
    dashboardId: string,
    body: {
      type: string;
      scopeKind?: string;
      scopeId?: string | null;
      config?: Record<string, unknown>;
    },
  ) =>
    request<{ widget: { id: string } }>(
      `/api/dashboards/${dashboardId}/widgets`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  deleteWidget: (id: string) =>
    request<{ ok: true }>(`/api/widgets/${id}`, { method: "DELETE" }),

  // Roadmap sharing
  projectTeamLinks: () =>
    request<{ links: Array<{ projectId: string; teamId: string }> }>(
      "/api/project-teams",
    ),
  createRoadmapShare: () =>
    request<{ token: string }>("/api/roadmap/share", { method: "POST" }),

  allMilestones: () =>
    request<{ milestones: import("@shared/types").MilestoneRow[] }>(
      `/api/milestones`,
    ),

  // Milestones
  listMilestones: (projectId: string) =>
    request<{ milestones: import("@shared/types").MilestoneRow[] }>(
      `/api/projects/${projectId}/milestones`,
    ),
  createMilestone: (
    projectId: string,
    body: { name: string; dueDate?: string },
  ) =>
    request<{ milestone: import("@shared/types").MilestoneRow }>(
      `/api/projects/${projectId}/milestones`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  updateMilestone: (
    id: string,
    body: { name?: string; dueDate?: string | null },
  ) =>
    request<{ ok: true }>(`/api/milestones/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteMilestone: (id: string) =>
    request<{ ok: true }>(`/api/milestones/${id}`, { method: "DELETE" }),

  // Epics
  listEpics: (projectId: string) =>
    request<{ epics: import("@shared/types").EpicRow[] }>(
      `/api/projects/${projectId}/epics`,
    ),
  createEpic: (
    projectId: string,
    body: { name: string; description?: string; color?: string },
  ) =>
    request<{ epic: import("@shared/types").EpicRow }>(
      `/api/projects/${projectId}/epics`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  updateEpic: (
    id: string,
    body: { name?: string; description?: string | null; color?: string | null },
  ) =>
    request<{ ok: true }>(`/api/epics/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteEpic: (id: string) =>
    request<{ ok: true }>(`/api/epics/${id}`, { method: "DELETE" }),

  // Teams (member groups)
  teams: () =>
    request<{ teams: import("@shared/types").TeamRow[] }>(`/api/teams`),
  createTeam: (name: string) =>
    request<{ team: { id: string; name: string } }>(`/api/teams`, {
      method: "POST",
      body: JSON.stringify({ name }),
    }),
  deleteTeam: (id: string) =>
    request<{ ok: true }>(`/api/teams/${id}`, { method: "DELETE" }),
  addTeamMember: (teamId: string, userId: string) =>
    request<{ ok: true }>(`/api/teams/${teamId}/members`, {
      method: "POST",
      body: JSON.stringify({ userId }),
    }),
  removeTeamMember: (teamId: string, userId: string) =>
    request<{ ok: true }>(`/api/teams/${teamId}/members/${userId}`, {
      method: "DELETE",
    }),
  projectTeams: (projectId: string) =>
    request<{ teams: Array<{ id: string; name: string }> }>(
      `/api/projects/${projectId}/teams`,
    ),
  setProjectTeams: (projectId: string, teamIds: string[]) =>
    request<{ ok: true }>(`/api/projects/${projectId}/teams`, {
      method: "PUT",
      body: JSON.stringify({ teamIds }),
    }),

  // Announcements
  announcements: () =>
    request<{ announcements: import("@shared/types").AnnouncementRow[] }>(
      `/api/announcements`,
    ),
  createAnnouncement: (body: {
    title: string;
    body?: string;
    pinned?: boolean;
    expiresAt?: string | null;
    teamId?: string | null;
  }) =>
    request<{ id: string }>(`/api/announcements`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  dismissAnnouncement: (id: string) =>
    request<{ ok: true }>(`/api/announcements/${id}/dismiss`, {
      method: "POST",
    }),
  deleteAnnouncement: (id: string) =>
    request<{ ok: true }>(`/api/announcements/${id}`, { method: "DELETE" }),

  // Documents (project knowledge base)
  listDocuments: (projectId: string) =>
    request<{ documents: import("@shared/types").DocumentRow[] }>(
      `/api/projects/${projectId}/documents`,
    ),
  getDocument: (id: string) =>
    request<{ document: import("@shared/types").DocumentRow }>(
      `/api/documents/${id}`,
    ),
  createDocument: (
    projectId: string,
    body: { kind?: string; title: string; body?: string },
  ) =>
    request<{ document: import("@shared/types").DocumentRow }>(
      `/api/projects/${projectId}/documents`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  updateDocument: (
    id: string,
    body: {
      title?: string;
      body?: string;
      status?: "proposed" | "accepted" | "superseded";
      supersedesId?: string | null;
      taskId?: string | null;
    },
  ) =>
    request<{ ok: true }>(`/api/documents/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  // Documents linked to a task (compact rows for the task's Documents panel).
  listTaskDocuments: (taskId: string) =>
    request<{
      documents: Array<{
        id: string;
        projectId: string;
        kind: string;
        title: string;
        taskId: string | null;
        updatedAt: string;
      }>;
    }>(`/api/tasks/${taskId}/documents`),
  deleteDocument: (id: string) =>
    request<{ ok: true }>(`/api/documents/${id}`, { method: "DELETE" }),
  documentVersions: (id: string) =>
    request<{
      versions: Array<{
        id: string;
        title: string;
        body: string;
        editedAt: string;
        editedByName: string | null;
      }>;
    }>(`/api/documents/${id}/versions`),

  // Custom fields
  listCustomFields: (projectId: string) =>
    request<{ fields: import("@shared/types").CustomFieldRow[] }>(
      `/api/projects/${projectId}/custom-fields`,
    ),
  createCustomField: (
    projectId: string,
    body: import("@shared/types").CreateCustomFieldInput,
  ) =>
    request<{ field: import("@shared/types").CustomFieldRow }>(
      `/api/projects/${projectId}/custom-fields`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  deleteCustomField: (id: string) =>
    request<{ ok: true }>(`/api/custom-fields/${id}`, { method: "DELETE" }),
  customFieldValues: (projectId: string) =>
    request<{
      values: Array<{
        fieldId: string;
        taskId: string;
        value: string | number | boolean | string[] | null;
      }>;
    }>(`/api/projects/${projectId}/custom-field-values`),
  setCustomFieldValue: (
    taskId: string,
    fieldId: string,
    value: string | number | boolean | string[] | null,
  ) =>
    request<{ ok: true }>(`/api/tasks/${taskId}/custom-fields/${fieldId}`, {
      method: "PUT",
      body: JSON.stringify({ value }),
    }),

  // Reactions
  reactions: (targetType: "task" | "comment", targetId: string) =>
    request<import("@shared/types").ReactionsResponse>(
      `/api/reactions/${targetType}/${targetId}`,
    ),
  toggleReaction: (
    targetType: "task" | "comment",
    targetId: string,
    emoji: string,
  ) =>
    request<{ reacted: boolean }>(
      `/api/reactions/${targetType}/${targetId}/toggle`,
      { method: "POST", body: JSON.stringify({ emoji }) },
    ),

  // Comments
  listComments: (taskId: string) =>
    request<{ comments: CommentRow[] }>(`/api/tasks/${taskId}/comments`),
  postComment: (taskId: string, body: string, parentId?: string) =>
    request<{ comment: CommentRow }>(`/api/tasks/${taskId}/comments`, {
      method: "POST",
      body: JSON.stringify({ body, parentId }),
    }),

  // My queues
  myTasks: () => request<{ tasks: TaskRow[] }>("/api/me/tasks"),
  watching: () => request<{ tasks: TaskRow[] }>("/api/me/watching"),

  // Global tasks — all tasks visible to the current user across all projects
  tasksAll: () =>
    request<{
      items: Array<TaskRow & { assigneeIds: string[]; watcherIds: string[] }>;
    }>("/api/tasks"),

  // Subtasks
  listSubtasks: (taskId: string) =>
    request<{ subtasks: TaskRow[] }>(`/api/tasks/${taskId}/subtasks`),
  createSubtask: (projectId: string, parentTaskId: string, title: string) =>
    request<{ task: TaskRow }>(`/api/projects/${projectId}/tasks`, {
      method: "POST",
      body: JSON.stringify({ title, priority: 3, parentTaskId }),
    }),
  // Shortcut used by the kanban + subtask toggles — same wire shape as patchTask.
  toggleTaskStatus: (taskId: string, status: TaskStatus) =>
    request<{ ok: true }>(`/api/tasks/${taskId}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),

  // Watchers — Task 2.2
  watchTask: (taskId: string) =>
    request<{ ok: true }>(`/api/tasks/${taskId}/watchers/me`, {
      method: "POST",
    }),
  unwatchTask: (taskId: string) =>
    request<{ ok: true }>(`/api/tasks/${taskId}/watchers/me`, {
      method: "DELETE",
    }),

  // Tags
  listTags: () => request<{ tags: TagRow[] }>("/api/tags"),
  createTag: (body: { name: string; color: string }) =>
    request<{ tag: TagRow }>("/api/tags", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  assignTag: (taskId: string, tagId: string) =>
    request<{ ok: true }>(`/api/tasks/${taskId}/tags`, {
      method: "POST",
      body: JSON.stringify({ tagId }),
    }),
  unassignTag: (taskId: string, tagId: string) =>
    request<{ ok: true }>(`/api/tasks/${taskId}/tags/${tagId}`, {
      method: "DELETE",
    }),

  // Attachments
  deleteAttachment: (id: string) =>
    request<{ ok: true }>(`/api/attachments/${id}`, { method: "DELETE" }),
  renameAttachment: (id: string, filename: string) =>
    request<{ ok: true }>(`/api/attachments/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ filename }),
    }),
  listProjectFiles: (projectId: string) =>
    request<{ files: ProjectFileRow[] }>(`/api/projects/${projectId}/files`),
  listAttachments: (taskId: string) =>
    request<{ attachments: AttachmentRow[] }>(
      `/api/tasks/${taskId}/attachments`,
    ),
  getAttachment: (id: string) =>
    request<{ url: string; filename: string; mimeType: string }>(
      `/api/attachments/${id}`,
    ),
  attachmentPresign: (body: {
    filename: string;
    mimeType: string;
    sizeBytes: number;
    taskId?: string;
    commentId?: string;
    projectId?: string;
  }) =>
    request<{ url: string; attachmentId: string }>("/api/attachments/presign", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  attachmentFinalize: (id: string) =>
    request<{ ok: true }>(`/api/attachments/${id}/finalize`, {
      method: "POST",
    }),

  // Search — drives the command palette. Returns FTS5-snippet HTML on
  // matched fields; the caller is responsible for sanitising before
  // dangerouslySetInnerHTML (see CommandPalette's `safeSnip`).
  search: (q: string) =>
    request<{
      projects: Array<{ id: string; name: string }>;
      tasks: Array<{
        id: string;
        project_id: string;
        title: string;
        snip: string;
      }>;
      comments: Array<{
        id: string;
        task_id: string;
        project_id: string;
        snip: string;
      }>;
      documents: Array<{
        id: string;
        project_id: string;
        kind: string;
        title: string;
      }>;
      wiki: Array<{
        id: string;
        project_id: string;
        slug: string;
        title: string;
      }>;
    }>(`/api/search?q=${encodeURIComponent(q)}`),

  // Wiki
  listWikiPages: (projectId: string) =>
    request<{ pages: import("@shared/types").WikiPageRow[] }>(
      `/api/projects/${projectId}/wiki`,
    ),
  getWikiPage: (id: string) =>
    request<{
      page: import("@shared/types").WikiPageRow;
      backlinks: Array<{ id: string; slug: string; title: string }>;
    }>(`/api/wiki/${id}`),
  createWikiPage: (
    projectId: string,
    body: import("@shared/types").CreateWikiPageInput,
  ) =>
    request<{ page: { id: string; slug: string } }>(
      `/api/projects/${projectId}/wiki`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  updateWikiPage: (
    id: string,
    body: import("@shared/types").UpdateWikiPageInput,
  ) =>
    request<{ ok: true }>(`/api/wiki/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteWikiPage: (id: string) =>
    request<{ ok: true }>(`/api/wiki/${id}`, { method: "DELETE" }),

  // Activity feed
  listActivity: (projectId: string, cursor?: string) =>
    request<{ items: ActivityItem[]; nextCursor: string | null }>(
      `/api/projects/${projectId}/activity?limit=50${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),
  // Per-task activity (raw camelCase rows). Used by TaskDetail's Activity tab.
  taskActivity: (taskId: string) =>
    request<TaskActivityResponse>(`/api/tasks/${taskId}/activity`),
  listActivityGlobal: (cursor?: string) =>
    request<{ items: ActivityItem[]; nextCursor: string | null }>(
      `/api/activity${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),

  // Team
  listTeam: () => request<{ members: TeamMemberRow[] }>("/api/team"),

  // Single team member — no dedicated endpoint exists yet, so fall back to
  // filtering the team list. Returns null when the userId is not in the
  // caller's visible team graph.
  teamMember: async (userId: string): Promise<TeamMemberRow | null> => {
    const data = await request<{ members: TeamMemberRow[] }>("/api/team");
    return data.members.find((m) => m.id === userId) ?? null;
  },

  // Inbox
  inbox: () => request<{ items: ActivityRow[] }>("/api/me/inbox"),
  inboxUnread: () => request<{ unread: number }>("/api/me/inbox/unread"),
  inboxMarkRead: (activityId: string) =>
    request<{ ok: true }>("/api/me/inbox/read", {
      method: "POST",
      body: JSON.stringify({ activityId }),
    }),
  inboxMarkAllRead: () =>
    request<{ ok: true; marked: number }>("/api/me/inbox/read-all", {
      method: "POST",
      body: JSON.stringify({}),
    }),

  // Files
  listFiles: (opts: {
    type?: "image" | "doc" | "other";
    projectId?: string;
    uploader?: string;
    q?: string;
    sort?: "name" | "size" | "uploaded_at" | "project" | "uploader";
    dir?: "asc" | "desc";
    page?: number;
    pageSize?: number;
  }) => {
    const p = new URLSearchParams();
    if (opts.type) p.set("type", opts.type);
    if (opts.projectId) p.set("projectId", opts.projectId);
    if (opts.uploader) p.set("uploader", opts.uploader);
    if (opts.q) p.set("q", opts.q);
    if (opts.sort) p.set("sort", opts.sort);
    if (opts.dir) p.set("dir", opts.dir);
    if (opts.page) p.set("page", String(opts.page));
    if (opts.pageSize) p.set("pageSize", String(opts.pageSize));
    const qs = p.toString();
    return request<{
      files: FileRow[];
      total: number;
      page: number;
      pageSize: number;
    }>(`/api/files${qs ? `?${qs}` : ""}`);
  },

  // Admin — instance role + invite management. Gated server-side by the
  // requireAdmin middleware; callers should hide UI when me.role !== 'admin'.
  adminListUsers: () =>
    request<{
      users: Array<{
        id: string;
        email: string;
        name: string | null;
        role: "admin" | "member";
        image: string | null;
        createdAt: string | number | null;
      }>;
      adminCount: number;
    }>("/api/admin/users"),
  adminSetRole: (id: string, role: "admin" | "member") =>
    request<{ user: { id: string; role: "admin" | "member" } }>(
      `/api/admin/users/${id}`,
      { method: "PATCH", body: JSON.stringify({ role }) },
    ),
  adminListInvites: (status: "pending" | "all" = "pending") =>
    request<{
      invites: Array<{
        id: string;
        email: string;
        invitedBy: string;
        invitedByName: string | null;
        invitedByEmail: string | null;
        expiresAt: string;
        consumedAt: string | null;
        createdAt: string;
      }>;
    }>(`/api/admin/invites?status=${status}`),
  adminCreateInvite: (email: string) =>
    request<{
      invite: { id: string; email: string; expiresAt: string };
      delivery: { ok: boolean; error?: string };
    }>("/api/admin/invites", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  adminResendInvite: (id: string) =>
    request<{ delivery: { ok: boolean; error?: string } }>(
      `/api/admin/invites/${id}/resend`,
      { method: "POST" },
    ),
  adminRevokeInvite: (id: string) =>
    request<{ ok: true }>(`/api/admin/invites/${id}`, { method: "DELETE" }),

  // Dashboard metrics
  metricsDashboard: () =>
    request<MetricsDashboardResponseT>("/api/metrics/dashboard"),

  // Team workload
  teamWorkload: () =>
    request<{
      members: Array<{
        userId: string;
        name: string;
        openCount: number;
        completedCount: number;
        projectCount: number;
        load: "light" | "balanced" | "heavy";
        committedEffort: number;
        capacity: number | null;
      }>;
    }>("/api/team/workload"),
  timeOff: () =>
    request<{
      timeOff: Array<{
        id: string;
        userId: string;
        startDate: string;
        endDate: string;
        reason: string;
      }>;
    }>("/api/team/time-off"),
  addTimeOff: (body: {
    userId?: string;
    startDate: string;
    endDate: string;
    reason?: string;
  }) =>
    request<{ id: string }>("/api/team/time-off", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteTimeOff: (id: string) =>
    request<{ ok: true }>(`/api/team/time-off/${id}`, { method: "DELETE" }),
  setUserCapacity: (userId: string, capacity: number | null) =>
    request<{ user: unknown }>(`/api/admin/users/${userId}`, {
      method: "PATCH",
      body: JSON.stringify({ capacity }),
    }),
  // Requirements
  listRequirements: (projectId: string) =>
    request<{ requirements: import("@shared/types").RequirementRow[] }>(
      `/api/projects/${projectId}/requirements`,
    ),
  createRequirement: (
    projectId: string,
    body: import("@shared/types").CreateRequirementInput,
  ) =>
    request<{ requirement: { id: string; reqNumber: number } }>(
      `/api/projects/${projectId}/requirements`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  updateRequirement: (
    id: string,
    body: import("@shared/types").UpdateRequirementInput,
  ) =>
    request<{ ok: true }>(`/api/requirements/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteRequirement: (id: string) =>
    request<{ ok: true }>(`/api/requirements/${id}`, { method: "DELETE" }),
  requirementTasks: (id: string) =>
    request<{
      tasks: Array<{ taskId: string; title: string; status: string }>;
    }>(`/api/requirements/${id}/tasks`),
  linkRequirementTask: (id: string, taskId: string) =>
    request<{ ok: true }>(`/api/requirements/${id}/tasks`, {
      method: "POST",
      body: JSON.stringify({ taskId }),
    }),
  unlinkRequirementTask: (id: string, taskId: string) =>
    request<{ ok: true }>(`/api/requirements/${id}/tasks/${taskId}`, {
      method: "DELETE",
    }),

  // Resource allocations
  listAllocations: () =>
    request<{ allocations: import("@shared/types").AllocationRow[] }>(
      "/api/allocations",
    ),
  createAllocation: (
    projectId: string,
    body: import("@shared/types").CreateAllocationInput,
  ) =>
    request<{ allocation: { id: string } }>(
      `/api/projects/${projectId}/allocations`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  deleteAllocation: (id: string) =>
    request<{ ok: true }>(`/api/allocations/${id}`, { method: "DELETE" }),

  // Every open task across the user's projects — for the Workload view.
  teamTasks: () =>
    request<{
      tasks: Array<{
        id: string;
        title: string;
        projectId: string;
        projectName: string;
        assigneeId: string | null;
        estimate: number | null;
        dueDate: string | null;
        startDate: string | null;
        status: string;
      }>;
    }>("/api/team/tasks"),

  // Recent projects — server-side ordered + enriched
  projectsRecent: (limit = 4) =>
    request<{ items: ProjectRow[] }>(
      `/api/projects/recent?limit=${encodeURIComponent(String(limit))}`,
    ),

  // Global activity feed (dashboard variant — alias for listActivityGlobal)
  activityGlobal: (cursor?: string) =>
    request<{ items: ActivityItem[]; nextCursor: string | null }>(
      `/api/activity${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),

  // Profile stats — Task 2.6 endpoint
  profileStats: () =>
    request<{
      completed: number;
      onTimeRate: number;
      activeProjects: number;
      commentsAuthored: number;
      monthly: Array<{ month: string; count: number }>;
    }>("/api/me/stats"),

  // My projects — returns all projects; Profile page uses them as "assigned projects"
  myProjects: async (): Promise<{ items: ProjectRow[] }> => {
    const data = await request<{ projects: ProjectRow[] }>("/api/projects");
    return { items: data.projects };
  },

  // Update current user — PUT /api/me. Persists `name`; phone/timezone/pronouns
  // are stored client-side (localStorage) as a fallback.
  updateMe: (input: {
    name?: string;
    phone?: string;
    timezone?: string;
    pronouns?: string;
  }) =>
    request<{ user: UserDTO }>("/api/me", {
      method: "PUT",
      body: JSON.stringify(input),
    }),

  // Sessions — Task 3.13
  mySessions: () =>
    request<{
      sessions: Array<{
        id: string;
        browser: string;
        os: string;
        ip: string | null;
        lastSeen: number | null;
        current: boolean;
      }>;
    }>("/api/me/sessions"),
  revokeSession: (id: string) =>
    request<{ ok: true }>(`/api/me/sessions/${id}/revoke`, {
      method: "POST",
    }),

  // Workspace — Task 2.4
  workspace: () =>
    request<{
      name: string;
      website: string;
      plan: string;
      effortUnit?: "points" | "hours";
    }>("/api/workspace"),
  updateWorkspace: (input: {
    name?: string;
    website?: string;
    plan?: string;
    effortUnit?: "points" | "hours";
  }) =>
    request<{
      name: string;
      website: string;
      plan: string;
      effortUnit?: "points" | "hours";
    }>("/api/workspace", {
      method: "PUT",
      body: JSON.stringify(input),
    }),

  // Avatar + workspace logo + attachment uploads. Single-call Worker-relayed
  // upload — the SPA streams the raw file body to the Worker, which writes
  // through the R2 binding (env.ATTACHMENTS). Works in local dev (miniflare
  // emulates R2 in-process) and production. The legacy three-step presign
  // flow still exists server-side as a future scaling lever for very large
  // files, but the SPA always uses the direct path now.
  uploadAvatar: async (file: File): Promise<string> => {
    const res = await fetch("/api/me/avatar", {
      method: "POST",
      body: file,
      headers: { "content-type": file.type },
      credentials: "same-origin",
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as {
        error?: { code?: string; message?: string };
      };
      throw new Error(
        body?.error?.message ??
          body?.error?.code ??
          `Upload failed (${res.status})`,
      );
    }
    const { url } = (await res.json()) as { url: string };
    return url;
  },

  uploadWorkspaceLogo: async (file: File): Promise<string> => {
    const res = await fetch("/api/workspace/logo", {
      method: "POST",
      body: file,
      headers: { "content-type": file.type },
      credentials: "same-origin",
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as {
        error?: { code?: string; message?: string };
      };
      throw new Error(
        body?.error?.message ??
          body?.error?.code ??
          `Upload failed (${res.status})`,
      );
    }
    const { url } = (await res.json()) as { url: string };
    return url;
  },

  uploadAttachment: async (
    parent: { taskId: string } | { projectId: string },
    file: File,
  ): Promise<{ id: string; url: string }> => {
    const params = new URLSearchParams({
      ...parent,
      filename: file.name,
    });
    const res = await fetch(`/api/attachments/upload?${params.toString()}`, {
      method: "POST",
      body: file,
      headers: { "content-type": file.type },
      credentials: "same-origin",
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as {
        error?: { code?: ErrorCode | "unknown"; message?: string };
      };
      throw new ApiError(
        res.status,
        body?.error?.code ?? "unknown",
        body?.error?.message ?? `Upload failed (${res.status})`,
      );
    }
    return (await res.json()) as { id: string; url: string };
  },

  // Notification preferences — Task 2.3
  notificationPrefs: () =>
    request<{
      taskAssigned: boolean;
      mentions: boolean;
      dueReminders: boolean;
      comments: boolean;
      projectUpdates: boolean;
      weeklyDigest: boolean;
    }>("/api/me/notification-prefs"),
  updateNotificationPrefs: (
    input: Partial<{
      taskAssigned: boolean;
      mentions: boolean;
      dueReminders: boolean;
      comments: boolean;
      projectUpdates: boolean;
      weeklyDigest: boolean;
    }>,
  ) =>
    request<{
      taskAssigned: boolean;
      mentions: boolean;
      dueReminders: boolean;
      comments: boolean;
      projectUpdates: boolean;
      weeklyDigest: boolean;
    }>("/api/me/notification-prefs", {
      method: "PUT",
      body: JSON.stringify(input),
    }),
};
